import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Divider } from '@mui/material';
import { getCityFromid, getCityScores, getCitySeasonality, getCityTemperature } from 'helpers/data';
import { useEffect, useState } from 'react';
import React from 'react';
import InterestChips from 'components/custom/InterestChips';
import Chart from "react-apexcharts"
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Typography, Card, CardContent, Grid, Table } from '@mui/joy';
import { generateCostLabel, generatePopularityLabel, generateRatingLabel } from '../city/CityCardTable';
import { getFirebaseImage } from 'helpers/Functions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Colors } from 'helpers/Colors';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function CityPage() {
  const { id } = useParams();
  const city = getCityFromid(id);
  console.log("id", id);
  const navigate = useNavigate();
  const [image, setImage] = useState();
  const [city1Data, setCity1Data] = useState([0, 0, 0, 0, 0, 0]);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const statsLabels = ['total', 'cost', 'family', 'fun', 'english speaking', 'nightlife'];

  let citySeasonalityData = Object.values(getCitySeasonality(city?.name));
  citySeasonalityData = citySeasonalityData.map(value => value > 1 ? 0.1 : value);

  const temperatureData = getCityTemperature(city?.name)
    .map((monthData, index) => ({
      x: months[index],
      y: [Math.round(parseFloat(monthData.low)), Math.round(parseFloat(monthData.high))]
    }));
  
  const updateData = () => {
    if (city?.name) {
      const cityData = getCityScores(city?.name);
      const numericValues = Object.values(cityData).filter(value => typeof value === 'number');
      setCity1Data(numericValues);
    }
  }

  useEffect(() => {
    updateData();
  }, [city?.name]);

  useEffect(() => {
    let wasUnmounted = false;
    getFirebaseImage(`${id}.jpg`).then((image) => {
      if (wasUnmounted) return;
      setImage(image);
    });
    return () => {
      wasUnmounted = true;
    };
  }, [id]);

  // Function to navigate back to Explore
  const handleBackClick = () => {
    navigate(-1);
  };

  const baseOptions = {
    title: {
      align: 'left',
    },
    xaxis: {
      labels: {
        show: true,
      },
    },
    dataLabels: {
      enabled: false,
    }
  };

  // Extend the base options for options (area chart)
  const options = {
    ...baseOptions,
    chart: {
      type: 'area',
    },
    title: {
      ...baseOptions.title,
      text: 'Seasonality',
    },
    xaxis: {
      ...baseOptions.xaxis,
      categories: months, // Using months as x-axis labels
      tickPlacement: 'on',
    },
    stroke: {
      curve: 'smooth',
    },
    colors: ['#43B97F'],
    tooltip: {
      y: {
        formatter: (value) => value.toFixed(3),
      },
    },
  };

  const options1 = {
    ...baseOptions,
    chart: {
      type: 'bar',
    },
    title: {
      ...baseOptions.title,
      text: 'General stats',
    },
    xaxis: {
      ...baseOptions.xaxis,
      categories: statsLabels,
    },
    dataLabels: {
      enabled: true,
    },
    colors: ['#43B97F'],
    fill: {
      opacity: 1,
    },
  };

  const options2 = {
    ...baseOptions,
    chart: {
      type: 'rangerea',
    },
    title: {
      ...baseOptions.title,
      text: 'Temperature',
    },
    stroke: {
      curve: 'smooth',
    },
    colors: ['#43B97F'],
    yaxis: {
      labels: {
        formatter: (val) => {
          return val + '°C'
        }
      }
    }
  };
  
  const series = [
    {
      name: 'Seasonality',
      data: citySeasonalityData, // Data for the bar chart
    },
  ];

  const series1 = [
    {
      name: 'score',
      data: city1Data,
    },
  ];

  const series2 = [
    {
      name: 'Temperature',
      data: temperatureData
    },
  ]

  return (
    <Box sx={{ m: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          height: "50vh",
          overflow: "hidden",
          margin: "10px auto",
          backgroundImage: `url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box sx={{
          alignSelf: "flex-start",
          margin: "20px 0 0 20px"
        }}>
          <Button onClick={handleBackClick}
            sx={{
              backgroundColor: Colors.green,
              '&:hover': {
                backgroundColor: Colors.darkGreen,
              },
            }}>
            <ArrowBackIcon sx={{
              color: Colors.white,
            }} />
          </Button>
        </Box>
      </Box>

      <Divider />

      <div style={{ marginTop: "2rem", marginBottom: "2rem" }}>
        <InterestChips interests={city?.interests}></InterestChips>
      </div>

      {/* First Grid */}
      <Grid container spacing={2} columns={12}>
        {/* First Column */}
        <Grid item xs={12} md={8} container>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 'lg' }}>
              <CardContent>
                <Typography level="title-md" sx={{ m: 1, fontWeight: 'xl', overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {city.name}, {city.country} {city.flag}
                </Typography>
                <Typography sx={{ m: 1, mt: 1 }}>
                  {city?.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ boxShadow: 'lg' }}>
              <Chart options={options} series={series} type="area" height={250} />
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ boxShadow: 'lg' }}>
              <Chart options={options1} series={series1} type="bar" height={250} />
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 'lg' }}>
              <Chart options={options2} series={series2} type="rangeArea" height={250} />
            </Card>
          </Grid>
        </Grid>

        {/* Second Column */}
        <Grid item xs={12} md={4} container spacing={2} direction="column">
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 'lg' }}>
              <Table borderAxis="none" variant="plain" color="neutral" size="sm" stickyHeader sx={{ textAlign: "center" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>Popularity</th>
                    <th style={{ textAlign: "center" }}>Cost</th>
                    <th style={{ textAlign: "center" }}>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{generatePopularityLabel(city)}</td>
                    <td>{generateCostLabel(city)}</td>
                    <td>{generateRatingLabel(city)}</td>
                  </tr>
                </tbody>
              </Table>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ boxShadow: 'lg' }}>
              <CardContent>
                <Typography level="title-md" sx={{ fontWeight: "xl", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  Main attractions in {city.name}
                </Typography>
                {city?.places.map((place, index) => (
                  <Typography key={index} level="body-s" sx={{ display: "flex", alignItems: "center" }}>
                    <li>{place}</li>
                  </Typography>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ boxShadow: 'lg' }}>
              <Typography level="title-md" sx={{ fontWeight: 'xl', overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                Top reasons to visit {city.name}
              </Typography>
              <CardContent>
                {city?.reasons.map((reason, index) => (
                  <Typography key={index} level="body-s" sx={{ display: 'flex', alignItems: 'center' }}>
                    <li>{reason}</li>
                  </Typography>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ boxShadow: 'lg' }}>
              <MapContainer center={[city.latitude, city.longitude]} zoom={14} style={{ height: "70vh", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[city.latitude, city.longitude]} icon={new L.Icon.Default()} />
              </MapContainer>
            </Card>
          </Grid>
        </Grid>
      </Grid>

    </Box>
  );
}

export default CityPage;
